import { useState, useEffect } from 'react';
import { getProductImage } from '../../utils/imageStorage';
import LocalImageUploader from '../../components/feature/LocalImageUploader';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useCart } from '../../hooks/useCart';

export default function ProduitBouleNoelPapierGrisArgent() {
  const productId = 'boule-noel-papier-gris-argent';
  const defaultImage = 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/9666bdfa71b5e42184d5a770c75e4383.png';
  const [productImage, setProductImage] = useState<string>(defaultImage);
  const [showImageUploader, setShowImageUploader] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [showNotification, setShowNotification] = useState(false);
  const { addItem } = useCart();

  useEffect(() => {
    const loadImage = async () => {
      const savedImage = await getProductImage(productId);
      if (savedImage) {
        setProductImage(savedImage);
      }
    };
    loadImage();
  }, []);

  const handleImageUpdate = (newImageUrl: string) => {
    setProductImage(newImageUrl || defaultImage);
    setShowImageUploader(false);
    window.location.reload();
  };

  // SEO avec React 19
  useEffect(() => {
    document.title = 'Boule de Noël en papier – Doux Gris & Argent (pièce unique) – Les Carnets de Lolotte';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Boule de Noël artisanale en papier plié gris et argent. Création unique faite main avec perles bois et ruban argenté. Décoration élégante et naturelle.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'boule de Noël, papier plié, gris argent, artisanal, fait main, pièce unique, décoration naturelle');
    }
    
    const linkCanonical = document.querySelector('link[rel="canonical"]');
    if (linkCanonical) {
      linkCanonical.setAttribute('href', 'https://lescarnetsdelolotte.fr/produit-boule-noel-papier-gris-argent');
    }
  }, []);

  const currentPrice = 4.00;

  const handleAddToCart = () => {
    addItem({
      id: productId,
      name: 'Boule de Noël en papier – Doux Gris & Argent (pièce unique)',
      price: currentPrice,
      quantity: quantity,
      image: productImage
    });
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);
  };

  const handleBuyNow = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-stripe-checkout-live`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            items: [
              {
                name: 'Boule de Noël en papier – Doux Gris & Argent (pièce unique)',
                price: currentPrice,
                quantity: quantity,
                image: productImage
              }
            ]
          })
        }
      );

      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Erreur lors de la création de la session:', error);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8EDEA]">
      <Header />

      <main className="flex-grow">
        {/* Notification */}
        {showNotification && (
          <div className="fixed top-24 right-6 bg-[#C6A996] text-white px-6 py-4 rounded-lg shadow-lg z-50 animate-fade-in">
            <div className="flex items-center gap-3">
              <i className="ri-check-line text-xl"></i>
              <span className="font-medium">Produit ajouté au panier !</span>
            </div>
          </div>
        )}

        {/* Breadcrumb */}
        <div className="bg-white border-b border-[#E8D5C4]">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center gap-2 text-sm text-[#8B7355]">
              <a href="/" className="hover:text-[#C6A996] transition-colors">Accueil</a>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <a href="/boutique" className="hover:text-[#C6A996] transition-colors">Boutique</a>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <a href="/noel" className="hover:text-[#C6A996] transition-colors">Collection de Noël</a>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-[#C6A996] font-medium">Boule de Noël en papier – Doux Gris & Argent</span>
            </div>
          </div>
        </div>

        {/* Product Section */}
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Image */}
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <img
                  src={productImage}
                  alt="Boule de Noël en papier – Doux Gris & Argent – Fait main"
                  className="w-full h-auto object-cover"
                />
              </div>
              <div className="absolute top-6 left-6 bg-[#C6A996] text-white px-4 py-2 rounded-full text-sm font-medium shadow-md">
                Pièce unique ✨
              </div>
              <button
                onClick={() => setShowImageUploader(!showImageUploader)}
                className="absolute top-6 right-6 bg-white/90 hover:bg-white text-[#8B7355] px-4 py-2 rounded-full text-sm font-medium shadow-md transition-colors whitespace-nowrap"
              >
                <i className="ri-image-edit-line mr-2"></i>
                Remplacer l'image
              </button>
              {showImageUploader && (
                <div className="mt-4">
                  <LocalImageUploader
                    productId={productId}
                    currentImage={productImage}
                    onImageSelect={handleImageUpdate}
                  />
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="lg:sticky lg:top-24">
              <div className="mb-4">
                <span className="inline-block bg-[#FFF8F5] text-[#C49B86] px-4 py-2 rounded-full text-sm font-medium">
                  Collection de Noël • Boules de Noël
                </span>
              </div>

              <h1 className="text-4xl font-serif text-[#8B7355] mb-4">
                Boule de Noël en papier – Doux Gris & Argent (pièce unique)
              </h1>

              <div className="flex items-baseline gap-3 mb-6">
                <span className="text-3xl font-bold text-[#C6A996]">{currentPrice} €</span>
              </div>

              <div className="prose prose-lg text-[#8B7355] mb-8 leading-relaxed">
                <p>
                  Cette boule de Noël est une création artisanale réalisée à la main en papier plié, dans des tons doux de gris clair et gris perlé. Elle est montée avec de petites perles en bois naturel et suspendue par un ruban scintillant couleur argent.
                </p>
                <p>
                  Son style épuré, élégant et lumineux s'intègre parfaitement dans une décoration de Noël naturelle, chic et apaisante.
                </p>
                <p>
                  Chaque pièce est fabriquée à la main dans mon atelier. Elle est unique et ne peut être reproduite à l'identique.
                </p>
                <ul className="space-y-2 mt-4">
                  <li className="flex items-start gap-2">
                    <span className="text-[#C6A996]">🎄</span>
                    <span>Une décoration de sapin sobre et élégante</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#C6A996]">🎁</span>
                    <span>Un petit cadeau artisanal délicat</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#C6A996]">✨</span>
                    <span>Une ambiance douce, naturelle et lumineuse</span>
                  </li>
                </ul>
              </div>

              {/* Quantity */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#8B7355] mb-3">
                  Quantité :
                </label>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-12 h-12 flex items-center justify-center bg-white border-2 border-[#E8D5C4] rounded-lg hover:border-[#C6A996] transition-colors whitespace-nowrap"
                  >
                    <i className="ri-subtract-line text-xl text-[#8B7355]"></i>
                  </button>
                  <input
                    type="number"
                    min="1"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-20 h-12 text-center border-2 border-[#E8D5C4] rounded-lg text-[#8B7355] font-semibold focus:outline-none focus:border-[#C6A996]"
                  />
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-12 h-12 flex items-center justify-center bg-white border-2 border-[#E8D5C4] rounded-lg hover:border-[#C6A996] transition-colors whitespace-nowrap"
                  >
                    <i className="ri-add-line text-xl text-[#8B7355]"></i>
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <button
                  onClick={handleAddToCart}
                  className="w-full bg-[#C6A996] text-white py-4 rounded-xl font-semibold hover:bg-[#B89985] transition-colors shadow-md whitespace-nowrap"
                >
                  <i className="ri-shopping-cart-line mr-2"></i>
                  Ajouter au panier
                </button>
                <button
                  onClick={handleBuyNow}
                  className="w-full bg-[#8B7355] text-white py-4 rounded-xl font-semibold hover:bg-[#7A6449] transition-colors shadow-md whitespace-nowrap"
                >
                  <i className="ri-flashlight-line mr-2"></i>
                  Acheter maintenant
                </button>
              </div>

              {/* Additional Info */}
              <div className="mt-8 space-y-4 bg-white rounded-xl p-6 border border-[#E8D5C4]">
                <div className="flex items-start gap-3">
                  <i className="ri-hand-heart-line text-2xl text-[#C6A996]"></i>
                  <div>
                    <h3 className="font-semibold text-[#8B7355] mb-1">Création 100 % faite main</h3>
                    <p className="text-sm text-[#8B7355]/80">Chaque pièce est unique et réalisée artisanalement</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-star-line text-2xl text-[#C6A996]"></i>
                  <div>
                    <h3 className="font-semibold text-[#8B7355] mb-1">Pièce unique</h3>
                    <p className="text-sm text-[#8B7355]/80">Ne peut être reproduite à l'identique</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-leaf-line text-2xl text-[#C6A996]"></i>
                  <div>
                    <h3 className="font-semibold text-[#8B7355] mb-1">Matières naturelles</h3>
                    <p className="text-sm text-[#8B7355]/80">Papier, bois, ruban - Légère et facile à suspendre</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Related Products */}
        <div className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl font-serif text-[#8B7355] text-center mb-12">
              Vous aimerez aussi
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  name: 'Boule de Noël en papier – Rouge & Naturel',
                  price: '4 €',
                  image: 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/30d563562a39e73a79c52a699e03bf8e.png',
                  link: '/produit-boule-noel-papier-rouge-naturel'
                },
                {
                  name: 'Boule de Noël en papier – Bleu & Pois Dorés',
                  price: '4 €',
                  image: 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/29824ce96cbebe40df2b2f4435a18104.png',
                  link: '/produit-boule-noel-papier-bleu-pois-dores'
                },
                {
                  name: 'Boule de Noël argent pailletée',
                  price: '4 €',
                  image: 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/1953d1495cd5e8d1c233a6c576865ce6.png',
                  link: '/produit-boule-noel-argent-pailletee'
                }
              ].map((product, index) => (
                <a
                  key={index}
                  href={product.link}
                  className="group bg-[#FFF9F7] rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all"
                >
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="font-semibold text-[#8B7355] mb-2 group-hover:text-[#C6A996] transition-colors">
                      {product.name}
                    </h3>
                    <p className="text-[#C6A996] font-bold">{product.price}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
